import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}
/* eslint-disable consistent-return */


import React, { cloneElement, useMemo, useContext } from 'react';
import { noop } from "../../utils/noop";
import { isFunction } from "../../utils/is-function";
import { PopoverContent } from "./PopoverContent";
import { usePopover } from "./usePopover";
import { PopoverContext } from "./PopoverContext";
import { theme } from "../../theme";
import { wrapEvent } from "../../utils/wrap-event";
import mergeRefs from 'react-merge-refs';
var PERMITTED_TAB_INDEXES = [-1, 0];
/**
 * Popover
 *
 * Handles displaying menus using absolute positioning,
 * triggered when the target is clicked.
 */

export function Popover(_ref) {
  var isShowingProp = _ref.isShowing,
      _ref$onRequestChange = _ref.onRequestChange,
      onRequestChange = _ref$onRequestChange === void 0 ? noop : _ref$onRequestChange,
      content = _ref.content,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? 'bottom' : _ref$placement,
      children = _ref.children,
      _ref$shouldUsePortal = _ref.shouldUsePortal,
      shouldUsePortal = _ref$shouldUsePortal === void 0 ? true : _ref$shouldUsePortal,
      _ref$shouldCloseOnEsc = _ref.shouldCloseOnEscape,
      shouldCloseOnEscape = _ref$shouldCloseOnEsc === void 0 ? true : _ref$shouldCloseOnEsc,
      _ref$shouldFocusOnMou = _ref.shouldFocusOnMount,
      shouldFocusOnMount = _ref$shouldFocusOnMou === void 0 ? true : _ref$shouldFocusOnMou,
      _ref$defaultShowing = _ref.defaultShowing,
      defaultShowing = _ref$defaultShowing === void 0 ? false : _ref$defaultShowing,
      initialFocusRef = _ref.initialFocusRef,
      className = _ref.className,
      anchorElement = _ref.anchorElement,
      anchorPosition = _ref.anchorPosition,
      tooltip = _ref.tooltip,
      _ref$zIndex = _ref.zIndex,
      zIndex = _ref$zIndex === void 0 ? theme.zIndices.popover : _ref$zIndex,
      colorProp = _ref.color,
      offset = _ref.offset,
      boundary = _ref.boundary,
      autoHide = _ref.autoHide;

  var _useContext = useContext(PopoverContext),
      parentColor = _useContext.color;

  var color = colorProp !== null && colorProp !== void 0 ? colorProp : parentColor;

  var _usePopover = usePopover({
    isShowing: isShowingProp,
    onRequestChange: onRequestChange,
    defaultShowing: defaultShowing,
    initialFocusRef: initialFocusRef,
    shouldFocusOnMount: shouldFocusOnMount,
    shouldCloseOnEscape: shouldCloseOnEscape,
    targetEl: anchorElement
  }),
      bindTarget = _usePopover.bindTarget,
      bindContent = _usePopover.bindContent,
      isShowing = _usePopover.isShowing,
      hidePopover = _usePopover.hidePopover;

  var popoverContext = useMemo(function () {
    return {
      isWithinPopover: true,
      onRequestClose: hidePopover,
      color: color
    };
  }, [color, hidePopover]);

  var renderChildren = function renderChildren() {
    if (!children) return null;
    var renderedChildren = isFunction(children) ? children({
      isShowing: isShowing
    }) : children;
    var child = React.Children.only(renderedChildren);

    var _ref2 = bindTarget,
        onKeyPress = _ref2.onKeyPress,
        onMouseDown = _ref2.onMouseDown,
        onMouseEnter = _ref2.onMouseEnter,
        onMouseLeave = _ref2.onMouseLeave,
        ref = _ref2.ref,
        otherTargetBindings = _objectWithoutProperties(_ref2, ["onKeyPress", "onMouseDown", "onMouseEnter", "onMouseLeave", "ref"]);

    var popoverChild = cloneElement(child, _objectSpread(_objectSpread({
      onKeyPress: wrapEvent(onKeyPress, child.props.onKeyPress),
      onMouseDown: wrapEvent(onMouseDown, child.props.onMouseDown),
      ref: mergeRefs([ref, child.ref]),
      onMouseEnter: wrapEvent(onMouseEnter, child.props.onMouseEnter),
      onMouseLeave: wrapEvent(onMouseLeave, child.props.onMouseLeave)
    }, otherTargetBindings), PERMITTED_TAB_INDEXES.includes(child.props.tabIndex) && {
      tabIndex: child.props.tabIndex
    }));

    if (!tooltip) {
      return popoverChild;
    } // If provided with a tooltip prop, we need to wrap our popover
    // child with the tooltip component too.


    var tooltipComponent = React.Children.only(tooltip);
    return cloneElement(tooltipComponent, {
      children: popoverChild
    });
  };

  return /*#__PURE__*/React.createElement(PopoverContext.Provider, {
    value: popoverContext
  }, /*#__PURE__*/React.createElement(PopoverContent, _extends({}, bindContent, {
    placement: placement,
    shouldUsePositioner: true,
    className: className,
    shouldUsePortal: shouldUsePortal,
    anchorPosition: anchorPosition,
    zIndex: zIndex,
    color: color,
    offset: offset,
    boundary: boundary,
    autoHide: autoHide
  }), content), renderChildren());
}